// app/components/PostCreator.tsx
'use client';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { supabaseBrowser } from '@/lib/supabaseBrowser';
import Link from 'next/link';
import Section from './Section';

// ── Tag structure ─────────────────────────────────────────────────────────────
export const TAG_STRUCTURE: Record<string, string[]> = {
  Business:      ['Naming', 'Pricing', 'Marketing', 'Product'],
  Design:        ['Logo & Visual', 'UX & Flow', 'Branding'],
  Tech:          ['Features', 'Dev', 'Tools'],
  Lifestyle:     ['Fashion & Beauty', 'Food & Drink', 'Health', 'Travel'],
  Hobbies:       ['Gaming', 'Outdoors', 'Crafts', 'Home & Garden'],
  Entertainment: ['Pop Culture', 'Celebrities', 'Sports', 'Media'],
  Social:        ['Relationships', 'Life Decisions', 'Opinions'],
  Other:         [], // write-in
};

export const BROAD_CATEGORIES = Object.keys(TAG_STRUCTURE);

type Opt = { label: string; text_option: string; imageFile?: File | null; existingImageUrl?: string | null };

type Props = {
  title: string;
  description: string;
  templateType: 'pick_one' | 'likert' | 'text' | 'yes_no' | 'rank_order';
  hasOptions?: boolean;
  fixedOptions?: { label: string; text_option: string }[];
  promptPlaceholder?: string;
};

function PostCreatorInner({
  title, description, templateType, hasOptions = false, fixedOptions, promptPlaceholder = 'What do you want to ask?',
  repostIdFromUrl,
}: Props & { repostIdFromUrl: string | null }) {
  const supabase = supabaseBrowser();
  const [prompt, setPrompt] = useState('');
  const [saving, setSaving] = useState(false);

  // Auth state — checked on mount so the wall shows up before the user fills the whole form, not after
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Repost support — if ?repost={postId} is present, we're recreating a closed post as a
  // fresh one. Prefill comes from the original post (owner-only read, works even if closed
  // per the "owners can read their own posts" policy), never from a fresh submission.
  const [repostSourceId, setRepostSourceId] = useState<string | null>(null);
  const [repostSeriesId, setRepostSeriesId] = useState<string | null>(null);
  const [repostBatchNumber, setRepostBatchNumber] = useState<number | null>(null);
  const [repostLoading, setRepostLoading] = useState(false);

  // Two-tier tag state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeBroad, setActiveBroad] = useState<string | null>(null);
  const [writeIn, setWriteIn] = useState('');

  const [contextImageFile, setContextImageFile] = useState<File | null>(null);
  const [contextImagePreview, setContextImagePreview] = useState<string | null>(null);
  const [existingContextImageUrl, setExistingContextImageUrl] = useState<string | null>(null);
  const [options, setOptions] = useState<Opt[]>(
    fixedOptions
      ? fixedOptions.map(o => ({ ...o, imageFile: null }))
      : hasOptions
      ? [{ label: 'A', text_option: '', imageFile: null }, { label: 'B', text_option: '', imageFile: null }]
      : []
  );

  // Multi-select toggle — only meaningful for 'pick_one' polls. Yes/No, ratings,
  // rank order, and open response don't have a sensible "pick more than one" state.
  const [allowMultiple, setAllowMultiple] = useState(false);

  const optionsLocked = !!fixedOptions;

  useEffect(() => {
    async function checkAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session?.user);
      setCheckingAuth(false);
    }
    checkAuth();
  }, []);

  // Repost prefill — runs once the repost id is known. Reads the original post (owner-only,
  // works even if it's closed) and carries forward its prompt, tags, images, and options,
  // along with the series/batch info needed to link this new post to the original.
  useEffect(() => {
    const repostId = repostIdFromUrl;
    if (!repostId) return;

    async function loadRepostSource(id: string) {
      setRepostLoading(true);
      const { data: source } = await supabase
        .from('posts')
        .select('prompt, tags, context_image_url, series_id, batch_number')
        .eq('id', id)
        .maybeSingle();

      if (source) {
        setRepostSourceId(id);
        setPrompt(source.prompt || '');
        setSelectedTags((source.tags as string[]) || []);
        if (source.context_image_url) setExistingContextImageUrl(source.context_image_url as string);
        // If the original post was itself already part of a series, keep that same series id
        // going. Otherwise this is the first repost, so the series starts at the original post's
        // own id — that id becomes the stable thread every future repost links back to.
        setRepostSeriesId((source.series_id as string) || id);
        setRepostBatchNumber(((source.batch_number as number) || 1) + 1);

        if (hasOptions) {
          const { data: sourceOptions } = await supabase
            .from('post_options')
            .select('label, text_option, image_url')
            .eq('post_id', id)
            .order('order_index', { ascending: true });
          if (sourceOptions && sourceOptions.length > 0) {
            setOptions(sourceOptions.map((o: any) => ({
              label: o.label,
              text_option: o.text_option || '',
              imageFile: null,
              existingImageUrl: o.image_url || null,
            })));
          }
        }
      }
      setRepostLoading(false);
    }

    loadRepostSource(repostId);
  }, [repostIdFromUrl]);

  function selectBroad(broad: string) {
    if (activeBroad === broad) {
      setActiveBroad(null);
    } else {
      setActiveBroad(broad);
    }
  }

  function selectSub(sub: string) {
    if (selectedTags.includes(sub)) {
      setSelectedTags(prev => prev.filter(t => t !== sub));
    } else if (selectedTags.length < 2) {
      setSelectedTags(prev => [...prev, sub]);
    }
  }

  function selectWriteIn() {
    const trimmed = writeIn.trim();
    if (!trimmed) return;
    if (selectedTags.includes(trimmed)) return;
    if (selectedTags.length < 2) {
      setSelectedTags(prev => [...prev, trimmed]);
      setWriteIn('');
      setActiveBroad(null);
    }
  }

  function removeTag(tag: string) {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  }

  function updateOption(i: number, value: string) {
    setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, text_option: value } : o));
  }

  function updateImage(i: number, file: File | null) {
    setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, imageFile: file } : o));
  }

  function addOption() {
    const nextLabel = String.fromCharCode('A'.charCodeAt(0) + options.length);
    setOptions(prev => [...prev, { label: nextLabel, text_option: '', imageFile: null }]);
  }

  function removeOption(i: number) {
    setOptions(prev => prev.filter((_, idx) => idx !== i));
  }

  function handleContextImage(file: File | null) {
    setContextImageFile(file);
    if (file) setContextImagePreview(URL.createObjectURL(file));
    else setContextImagePreview(null);
  }

  async function uploadImage(file: File, path: string): Promise<string | null> {
    const { error } = await supabase.storage.from('noisegate').upload(path, file, { upsert: true });
    if (error) { alert(`Upload failed: ${error.message}`); return null; }
    const { data } = supabase.storage.from('noisegate').getPublicUrl(path);
    return data.publicUrl;
  }

  async function create() {
    if (!isLoggedIn) { alert('You need to be logged in to publish a post.'); return; }
    if (!prompt.trim()) return alert('Add a prompt');
    if (hasOptions && !optionsLocked && options.some(o => !o.text_option.trim())) return alert('Fill in all options');
    setSaving(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      setSaving(false);
      setIsLoggedIn(false);
      alert('You need to be logged in to publish a post.');
      return;
    }

    // A random namespace for this post's images, generated before the post exists server-side.
    // We can't use the post's real id yet since creation now happens in the API route below.
    const uploadNamespace = crypto.randomUUID();

    let contextImageUrl: string | null = existingContextImageUrl;
    if (contextImageFile) {
      const ext = contextImageFile.name.split('.').pop();
      contextImageUrl = await uploadImage(contextImageFile, `post-images/${uploadNamespace}/context.${ext}`);
    }

    let optionPayload: { label: string; text_option: string; image_url: string | null }[] = [];
    if (hasOptions && options.length > 0) {
      optionPayload = await Promise.all(options.map(async (o) => {
        let imageUrl: string | null = o.existingImageUrl ?? null;
        if (o.imageFile) {
          const ext = o.imageFile.name.split('.').pop();
          imageUrl = await uploadImage(o.imageFile, `post-images/${uploadNamespace}/${o.label}.${ext}`);
        }
        return { label: o.label, text_option: o.text_option.trim(), image_url: imageUrl };
      }));
    }

    try {
      const res = await fetch('/api/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          templateType,
          prompt: prompt.trim(),
          tags: selectedTags,
          contextImageUrl,
          options: optionPayload,
          allowMultiple: templateType === 'pick_one' ? allowMultiple : false,
          // Present only when this post was created via "Repost as new" from a closed post.
          // /api/posts/route.ts needs to accept these two fields and write them straight to
          // the new row so the repost is linked to the original as part of the same series.
          ...(repostSourceId ? { seriesId: repostSeriesId, batchNumber: repostBatchNumber } : {}),
        }),
      });

      const data = await res.json();
      setSaving(false);

      if (!res.ok) {
        alert(data.error || 'Something went wrong publishing your post. Please try again.');
        return;
      }

      // ?created=1 lets the post detail page show the one-time
      // post-creation success message ("Live! Here's what others are
      // asking") only on this first landing, not on every later visit to
      // your own post.
      location.href = `/post/${data.postId}?created=1`;
    } catch (err) {
      setSaving(false);
      alert('Something went wrong publishing your post. Please try again.');
    }
  }

  const maxed = selectedTags.length >= 2;

  return (
    <div className="space-y-4">
      <Section>
        <h1 className="h1">{title}</h1>
        <p className="muted">{description}</p>
        <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', marginTop: '6px' }}>
          Posts stay open for 14 days. You can renew it before it closes, or repost it as a fresh round afterward — both from the post's own page.
        </p>
      </Section>

      {repostSourceId && (
        <Section>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: 'var(--green-glow)', border: '1px solid var(--green-dim)', borderRadius: '10px',
            padding: '10px 16px', fontSize: '13px', color: 'var(--text)',
          }}>
            <span>🌱</span>
            <span>
              {repostLoading
                ? 'Loading your previous post…'
                : `This is round ${repostBatchNumber} — prefilled from your earlier post. Feel free to change anything before publishing.`}
            </span>
          </div>
        </Section>
      )}

      {!checkingAuth && !isLoggedIn && (
        <Section>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px 12px',
            background: 'var(--amber-dim)', border: '1px solid var(--amber)', borderRadius: '10px',
            padding: '12px 16px',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>
              You'll need to be logged in to publish a post.
            </span>
            <Link href="/login" className="btn btn-primary" style={{ fontSize: '13px', flexShrink: 0, textDecoration: 'none' }}>
              Log in
            </Link>
          </div>
        </Section>
      )}

      <Section>
        <div className="field">
          <label className="label" htmlFor="post-prompt">Prompt</label>
          <textarea id="post-prompt" rows={3} value={prompt} onChange={e => setPrompt(e.target.value)}
            className="input" placeholder={promptPlaceholder} />
        </div>

        {/* Context image */}
        <div className="field" style={{ marginTop: '16px' }}>
          <label className="label">Context image <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontWeight: 400 }}>· optional</span></label>
          <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: '4px' }}>Add an image to give respondents more context — a design, screenshot, photo, etc.</p>
          <p style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: '8px' }}>Shown exactly as uploaded, never cropped — real signal starts with seeing the real thing.</p>
          {contextImagePreview ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={contextImagePreview} alt="Context preview" style={{ maxHeight: '200px', maxWidth: '100%', borderRadius: '8px', border: '1px solid var(--border)', objectFit: 'contain', background: 'var(--surface-raised)' }} />
              <button type="button" onClick={() => handleContextImage(null)} style={{
                position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', color: 'white',
                border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '12px',
              }}>✕</button>
            </div>
          ) : (
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
              border: '1px dashed var(--border)', borderRadius: '8px', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: '13px', background: 'var(--surface-raised)',
            }}>
              <span>+ Add context image</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => handleContextImage(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </div>

        {/* Two-tier tag selector */}
        <div className="field" style={{ marginTop: '16px' }}>
          <label className="label">
            Tags <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontWeight: 400 }}>· pick up to 2</span>
          </label>

          {/* Selected tags */}
          {selectedTags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', marginTop: '6px' }}>
              {selectedTags.map(tag => (
                <span key={tag} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                  background: 'var(--green-glow)', border: '1px solid var(--green)', color: 'var(--green)',
                }}>
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)',
                    padding: 0, fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center',
                  }}>✕</button>
                </span>
              ))}
            </div>
          )}

          {/* Broad categories */}
          {!maxed && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
              {BROAD_CATEGORIES.map(broad => {
                const isActive = activeBroad === broad;
                return (
                  <button key={broad} type="button" onClick={() => selectBroad(broad)} style={{
                    padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                    cursor: 'pointer',
                    background: isActive ? 'var(--indigo-glow)' : 'var(--surface-raised)',
                    border: `1px solid ${isActive ? 'var(--indigo)' : 'var(--border)'}`,
                    color: isActive ? 'var(--indigo)' : 'var(--text-muted)',
                    transition: 'all 0.15s', fontFamily: 'DM Sans, sans-serif',
                  }}>
                    {broad}
                  </button>
                );
              })}
            </div>
          )}

          {/* Subcategories */}
          {activeBroad && !maxed && (
            <div style={{
              marginTop: '10px', padding: '12px 14px',
              background: 'var(--surface-raised)', border: '1px solid var(--border)',
              borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                {activeBroad}
              </div>

              {activeBroad === 'Other' ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={writeIn}
                    onChange={e => setWriteIn(e.target.value.slice(0, 20))}
                    onKeyDown={e => e.key === 'Enter' && selectWriteIn()}
                    className="input"
                    placeholder="Describe your tag (max 20 chars)"
                    aria-label="Custom tag"
                    style={{ flex: 1, fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    onClick={selectWriteIn}
                    disabled={!writeIn.trim()}
                    className="btn btn-primary"
                    style={{ fontSize: '12px', flexShrink: 0 }}
                  >
                    Add
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {TAG_STRUCTURE[activeBroad].map(sub => {
                    const selected = selectedTags.includes(sub);
                    return (
                      <button key={sub} type="button" onClick={() => selectSub(sub)} style={{
                        padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 500,
                        cursor: 'pointer',
                        background: selected ? 'var(--green-glow)' : 'var(--surface)',
                        border: `1px solid ${selected ? 'var(--green)' : 'var(--border)'}`,
                        color: selected ? 'var(--green)' : 'var(--text-muted)',
                        transition: 'all 0.15s', fontFamily: 'DM Sans, sans-serif',
                      }}>
                        {sub}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {hasOptions && (
          <div className="mt-4 space-y-3">
            <div className="text-sm font-semibold">Options</div>

            {/* Multi-select toggle — pick_one polls only */}
            {templateType === 'pick_one' && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                color: 'var(--text-muted)', cursor: 'pointer', padding: '8px 0',
              }}>
                <input
                  type="checkbox"
                  checked={allowMultiple}
                  onChange={e => setAllowMultiple(e.target.checked)}
                  style={{ accentColor: 'var(--green)' }}
                />
                Allow multiple answers
                <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', fontSize: '11px' }}>
                  — respondents can pick more than one option
                </span>
              </label>
            )}

            {options.map((o, i) => (
              <div key={i} className="field space-y-1">
                <label className="label" htmlFor={`option-${i}`}>Option {o.label}</label>
                {optionsLocked ? (
                  <div className="input bg-gray-50 text-gray-500">{o.text_option}</div>
                ) : (
                  <>
                    <input id={`option-${i}`} value={o.text_option} onChange={e => updateOption(i, e.target.value)}
                      className="input" placeholder={`Text for option ${o.label}`} />
                    <div className="flex items-center gap-2 mt-1">
                      <label className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                        {o.imageFile ? `📎 ${o.imageFile.name}` : '+ Add image (optional)'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => updateImage(i, e.target.files?.[0] ?? null)} />
                      </label>
                      {o.imageFile && (
                        <button type="button" onClick={() => updateImage(i, null)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                      )}
                    </div>
                    {o.imageFile && (
                      <img src={URL.createObjectURL(o.imageFile)} alt={`Preview ${o.label}`}
                        className="mt-1 h-20 w-20 rounded object-contain border" style={{ background: 'var(--surface-raised)' }} />
                    )}
                  </>
                )}
              </div>
            ))}
            {!optionsLocked && (
              <button type="button" onClick={addOption} className="btn btn-ghost">+ Add option</button>
            )}
            {!optionsLocked && options.length > 2 && (
              <button type="button" onClick={() => removeOption(options.length - 1)} className="btn btn-ghost text-red-400">- Remove last option</button>
            )}
          </div>
        )}

        <div className="mt-4">
          <button onClick={create} disabled={saving || !isLoggedIn} className="btn btn-primary">
            {saving ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </Section>
    </div>
  );
}

// useSearchParams() opts a page out of static prerendering unless it's wrapped in
// its own Suspense boundary — isolating it here means every page that just renders
// <PostCreator /> doesn't need to know or care about that, it stays a plain drop-in.
function PostCreatorWithParams(props: Props) {
  const searchParams = useSearchParams();
  return <PostCreatorInner {...props} repostIdFromUrl={searchParams.get('repost')} />;
}

export default function PostCreator(props: Props) {
  return (
    <Suspense fallback={
      <div className="space-y-4">
        <Section>
          <h1 className="h1">{props.title}</h1>
          <p className="muted">{props.description}</p>
        </Section>
      </div>
    }>
      <PostCreatorWithParams {...props} />
    </Suspense>
  );
}
